import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import GenericStatusComponent from '../../genericComponents/GenericStatusComponent'
import GenericAvatarComponent from '../../genericComponents/GenericAvatarComponent'
import Icons from '../../assets/icons'
import CopyContentGenericComponent from '../../genericComponents/CopyContentGenericComponent'
import TextWithTooltip from '../../genericComponents/TextWithTooltip'
import ParentModal from '../../genericComponents/ParentModal'
import NoDataFound from '../../genericComponents/NoDataFound'
import ViewProductDetailsV2 from '../digicatalogue/viewDetails/ViewProductDetailsV2'
import CommentBoxModalWrapper from '../../genericComponents/oldGenericComponents/tableHelperComponents/comments/CommentBoxModalWrapper'
import GenericMediaViewModal from '../../genericComponents/media/GenericMediaViewModal'
import GenericFileShareModal from '../../genericComponents/media/GenericFileShareModal'
import TaskPreviewModal from './TaskPreviewModal'
import LanguageTranslator from '../../locals/LanguageTranslator'
import { dispatchHandler } from '../../helper/apiHelperFunctions'
import { getPermissionKey } from '../../helper/permissions'
import { parseJwt } from '../../helper/genericFunction'
import { modifyDataForDocumentViewerUrlInsertion, getFileTypeFromUrl, getFileNameFromExt } from '../../helper/helperTableFunctions'
import { getPayloadFromURLParams, setPayloadAsURLParams } from '../../helper/URLParamsHelper'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { Eye, EyeOff, Share2, MoreVertical, ChevronDown, Phone, Calendar, Hash, LayoutGrid, Plus, Equal, ChevronsUp, AlertTriangle, Check, CheckCheck, Search, Link2, User, Trash2 } from 'lucide-react'
import '../../styles/stylesheets/DigiCatalogue/ProductCatalogue.scss'

dayjs.extend(utc)

// Keys that, in addition to plain display, open a related view (product/document) when clicked
const FIELD_VIEW_MAP = {
    product_id: 'product',
    gsl_po: 'document',
}

const PRIORITY_STYLE_MAP = {
    LOW: 'bg-[#EAF2FF] text-[#2969FF]',
    MEDIUM: 'bg-[#FFF4E0] text-[#B7791F]',
    HIGH: 'bg-[#FDEBEA] text-[#F04438]',
    URGENT: 'bg-[#FDEBEA] text-[#F04438]',
}

const PRIORITY_ICON_MAP = {
    LOW: ChevronDown,
    MEDIUM: Equal,
    HIGH: ChevronsUp,
    URGENT: AlertTriangle,
}


const USERS_SCOPE_HEADER = getPermissionKey('common', 'commonFunctionality', 'view')

const buildUsersSearchPayload = (search) => ({
    type: 1,
    pageNo: 1,
    sortedBy: '',
    sortedIn: '',
    search: search || '',
    filter: {},
    pageSize: 30,
    displayName: 'MANAGE_USERS',
})

const TaskManagerDetailPanel = (props) => {
    const { task, priorityOptions = [], scrollToCommentId } = props
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const moduleUsersData = useSelector((state) => state?.administrationStore?.getModuleUsersData)
    const reassignUsersData = useSelector((state) => state?.taskManagerStore?.getReassignUsersData)
    const taskWatchersData = useSelector((state) => state?.taskManagerStore?.getTaskWatchersData)
    const addTaskWatcherData = useSelector((state) => state?.taskManagerStore?.addTaskWatcherData)
    const removeTaskWatcherData = useSelector((state) => state?.taskManagerStore?.removeTaskWatcherData)
    const completeTaskData = useSelector((state) => state?.taskManagerStore?.completeTaskData)
    const reassignTaskData = useSelector((state) => state?.taskManagerStore?.reassignTaskData)
    const childTaskDetailsData = useSelector((state) => state?.taskManagerStore?.getChildTaskDetailsData)
    const currentUserId = parseJwt(sessionStorage.getItem('token'))?.jti
    const [priorityOpen, setPriorityOpen] = useState(false)
    const [watchOverride, setWatchOverride] = useState(null)
    const [watchDropdownOpen, setWatchDropdownOpen] = useState(false)
    const [addWatcherOpen, setAddWatcherOpen] = useState(false)
    const [watcherSearch, setWatcherSearch] = useState('')
    const [moreMenuOpen, setMoreMenuOpen] = useState(false)
    const [reassignSearch, setReassignSearch] = useState('')
    const [childTasksOpen, setChildTasksOpen] = useState(false)
    const [childTaskPreviewId, setChildTaskPreviewId] = useState(null)
    const [selectedModal, setSelectedModal] = useState(null)
    const [selectedData, setSelectedData] = useState(null)
    const watchWrapperRef = useRef(null)
    const moreMenuRef = useRef(null)
    const priorityRef = useRef(null)
    const childTasksRef = useRef(null)
    const userSearchDebounceRef = useRef(null)
    const hasFetchedAllUsersRef = useRef(false)
    const reassignSearchDebounceRef = useRef(null)
    const hasFetchedReassignUsersRef = useRef(false)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (watchWrapperRef.current && !watchWrapperRef.current.contains(event.target)) {
                setWatchDropdownOpen(false)
            }
            if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
                setMoreMenuOpen(false)
            }
            if (priorityRef.current && !priorityRef.current.contains(event.target)) {
                setPriorityOpen(false)
            }
            if (childTasksRef.current && !childTasksRef.current.contains(event.target)) {
                setChildTasksOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Clear any leftover reassign search text whenever the more-menu closes, however it closes
    // (outside click, re-toggling the button, or after an action), so it doesn't silently filter
    // out candidates the next time the menu opens
    useEffect(() => {
        if (!moreMenuOpen) setReassignSearch('')
    }, [moreMenuOpen])

    // Load the current watcher list whenever the selected task changes; the user directory
    // (for name/initials lookup) only needs to be fetched once per panel session and is reused
    // across tasks from then on, instead of refetching on every task click.
    useEffect(() => {
        setWatchOverride(null)
        if (task?.id) {
            dispatchHandler(dispatch, "getTaskWatchersRequest", { taskId: task.id })
            if (!hasFetchedAllUsersRef.current) {
                hasFetchedAllUsersRef.current = true
                dispatchHandler(dispatch, "getModuleUsersRequest", buildUsersSearchPayload(''), USERS_SCOPE_HEADER)
            }
        }
    }, [task?.id])

    // Load the full user list as soon as the watcher dropdown opens, unless it's already cached
    useEffect(() => {
        if (watchDropdownOpen && !hasFetchedAllUsersRef.current) {
            hasFetchedAllUsersRef.current = true
            dispatchHandler(dispatch, "getModuleUsersRequest", buildUsersSearchPayload(''), USERS_SCOPE_HEADER)
        }
    }, [watchDropdownOpen])

    // Re-search (debounced) as the user types a name
    useEffect(() => {
        if (!addWatcherOpen) return
        if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current)
        userSearchDebounceRef.current = setTimeout(() => {
            dispatchHandler(dispatch, "getModuleUsersRequest", buildUsersSearchPayload(watcherSearch), USERS_SCOPE_HEADER)
        }, 300)
        return () => clearTimeout(userSearchDebounceRef.current)
    }, [watcherSearch, addWatcherOpen])

    // Load the reassign candidates (own dedicated slice, independent of the watcher search above)
    // as soon as the more-menu opens, then re-search (debounced) as the user types a name
    useEffect(() => {
        if (!moreMenuOpen) return
        if (reassignSearchDebounceRef.current) clearTimeout(reassignSearchDebounceRef.current)
        const delay = hasFetchedReassignUsersRef.current ? 300 : 0
        reassignSearchDebounceRef.current = setTimeout(() => {
            hasFetchedReassignUsersRef.current = true
            dispatchHandler(dispatch, "getReassignUsersRequest", buildUsersSearchPayload(reassignSearch), USERS_SCOPE_HEADER)
        }, delay)
        return () => clearTimeout(reassignSearchDebounceRef.current)
    }, [reassignSearch, moreMenuOpen])

    // After successfully adding or removing a watcher, refresh the watcher list to reflect it
    useEffect(() => {
        if (addTaskWatcherData?.isSuccess && task?.id) {
            dispatchHandler(dispatch, "getTaskWatchersRequest", { taskId: task.id })
        }
    }, [addTaskWatcherData?.isSuccess])

    useEffect(() => {
        if (removeTaskWatcherData?.isSuccess && task?.id) {
            dispatchHandler(dispatch, "getTaskWatchersRequest", { taskId: task.id })
        }
    }, [removeTaskWatcherData?.isSuccess])

    // After closing or reassigning the task, refresh its details so the status/assignee reflect the change
    useEffect(() => {
        if (completeTaskData?.isSuccess && task?.id) {
            dispatchHandler(dispatch, "getTaskDetailsRequest", { id: task.id })
        }
    }, [completeTaskData?.isSuccess])

    useEffect(() => {
        if (reassignTaskData?.isSuccess && task?.id) {
            dispatchHandler(dispatch, "getTaskDetailsRequest", { id: task.id })
            // Backend updates watcher data as part of reassignment - refresh it too
            dispatchHandler(dispatch, "getTaskWatchersRequest", { taskId: task.id })
        }
    }, [reassignTaskData?.isSuccess])

    if (!task) {
        return <div className="flex items-center justify-center h-full text-fontSlateGrey text-sm">Select a task to view details</div>
    }

    const PriorityIcon = PRIORITY_ICON_MAP[task.priority]
    const taskNo = task.task_number || task.taskNo || 'NA'
    const childTasks = task.childTasks || []

    const previewResource = childTaskDetailsData?.data?.resource
    const childTaskPreview = (childTaskPreviewId && previewResource?.task && String(previewResource.task.id) === String(childTaskPreviewId))
        ? { ...previewResource.task, linkedData: previewResource.linkedData, childTasks: previewResource.childTasks }
        : null

    const handleChildTaskClick = (childTask) => {
        setChildTasksOpen(false)
        if (props.onChildTaskSelect && String(childTask.current_assignee_id) === String(currentUserId)) {
            props.onChildTaskSelect(childTask.id, childTask)
        } else {
            dispatchHandler(dispatch, "getChildTaskDetailsRequest", { id: childTask.id })
            setChildTaskPreviewId(childTask.id)
        }
    }

    const closeChildTaskPreview = () => {
        setChildTaskPreviewId(null)
    }
    const dueDate = (task.due_at ? dayjs(task.due_at).utcOffset(330).format('D MMMM YYYY, h:mm A') : task.dueDate) || 'NA'
    const creationDate = (task.created_at ? dayjs(task.created_at).utcOffset(330).format('D MMMM YYYY, h:mm A') : task.creationDate) || 'NA'

    const isSystemCreated = String(task.created_by) === '-10'
    const rawAssignedBy = task.linkedData?.assignedBy
    const assignedBy = isSystemCreated
        ? { initials: 'SY', name: 'System', role: 'Automated', mobile: null, email: null }
        : rawAssignedBy
            ? { name: rawAssignedBy.username, mobile: rawAssignedBy.mobilenumber, email: rawAssignedBy.email }
            : null

    const userOptions = (moduleUsersData?.data?.resource || []).map((user) => ({
        userId: user.id,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || '..',
        initials: (`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`).toUpperCase() || '..',
    }))

    const userLookup = {}
    userOptions.forEach((person) => { userLookup[person.userId] = person })

    const watchers = (taskWatchersData?.data?.resource || [])
        .filter((watcher) => watcher.is_active)
        .map((watcher) => ({
            userId: watcher.user_id,
            name: watcher.user?.username || userLookup[watcher.user_id]?.name || watcher.user_id || '..',
        }))

    const filteredSuggestions = userOptions.filter(
        (person) => !watchers.some((watcher) => watcher.userId === person.userId)
    )

    const reassignOptions = (reassignUsersData?.data?.resource || []).map((user) => ({
        userId: user.id,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || '..',
    }))

    const reassignCandidates = reassignOptions.filter(
        (person) => String(person.userId) !== String(task.current_assignee_id)
    )

    const entityEntry = task.linkedData?.entityData?.[0]
    const detailsResource = { ...task, ...(entityEntry?.row || {}) }
    const detailFields = Object.entries(entityEntry?.labeledKeys || {}).map(([key, label]) => ({
        key,
        label,
        view: FIELD_VIEW_MAP[key],
        copyable: !!FIELD_VIEW_MAP[key],
    }))

    const commentDocumentId = task?.id != null ? Number(task.id) : null
    const commentDocumentNo = task?.task_number || task?.taskNo || (commentDocumentId != null ? `TSK-${commentDocumentId}` : null)
    const commentItem = { taskCommentId: commentDocumentId, taskCommentNo: commentDocumentNo, documentType: 'TASK' }
    const commentConfig = {
        disabled: false,
        forcePrivate: true,
        configKeys: {
            documentId: 'taskCommentId',
            documentNumber: 'taskCommentNo',
            documentType: 'documentType',
        },
    }

    const mediaUrls = entityEntry?.row?.mediaUrls || []
    const mediaFilesData = mediaUrls.map((url) => ({
        fileUrl: url,
        fileType: getFileNameFromExt(getFileTypeFromUrl(url)),
        fileName: '',
    }))
    const firstMedia = mediaFilesData[0]
    const isFirstMediaImage = firstMedia?.fileType === 'image'

    const getChildComponent = () => {
        switch (selectedModal) {
            case 'productView': return ViewProductDetailsV2
            case 'mediaView': return GenericMediaViewModal
            case 'shareDataModal': return GenericFileShareModal
            default: return null
        }
    }

    const getShareUrl = () => {
        const shareParams = new URLSearchParams()
        shareParams.set('taskId', JSON.stringify(task.id))
        return `${window.location.origin}${window.location.pathname}#${location.pathname}?${shareParams.toString()}`
    }

    const getModalWrapperClass = () => {
        switch (selectedModal) {
            case 'productView': return 'create-cost-sheet-modal viewProductsDetailsModal viewProductsDetailsModalNew'
            default: return ''
        }
    }

    const getParentChildProps = () => {
        switch (selectedModal) {
            case 'productView':
                return {
                    closeModal: () => setSelectedModal(null),
                    selectedData,
                    viewPageType: 'product',
                    activeTab: 'REQUESTED',
                    permissionsObj: getPermissionKey('purchase', 'product', 'view'),
                    animationType: 'bottom',
                    parentModalOuterMostClassName: 'flex items-center justify-center',
                    postitionProps: { position: 'relative' },
                    dimensions: { width: '80vw', height: '90vh', maxHeight: '740px', maxWidth: '1140px' },
                }
            case 'mediaView':
                return {
                    closeModal: () => setSelectedModal(null),
                    animationType: 'bottom',
                    parentModalOuterMostClassName: 'flex items-center justify-center',
                    postitionProps: { position: 'relative' },
                    dimensions: { width: '100vw', height: '100vh', backgroundColor: 'transparent' },
                    filesData: mediaFilesData,
                }
            case 'shareDataModal': {
                const shareUrl = getShareUrl()
                return {
                    closeModal: () => setSelectedModal(null),
                    animationType: 'bottom',
                    parentModalOuterMostClassName: 'flex items-center justify-center',
                    postitionProps: { position: 'relative' },
                    dimensions: { width: 'auto', height: 'auto', borderRadius: 5 },
                    fileUrl: shareUrl,
                    urlOnly: shareUrl,
                }
            }
            default:
                return {}
        }
    }

    const handleFieldClick = (field) => {
        if (field.view === 'product') {
            setSelectedData({ productId: detailsResource[field.key] })
            setSelectedModal('productView')
        } else if (field.view === 'document') {
            const obj = modifyDataForDocumentViewerUrlInsertion('encode', {
                documentId: detailsResource[field.key],
                documentNo: detailsResource[field.key],
                documentType: 'PO',
            })
            setPayloadAsURLParams({
                data: { ...getPayloadFromURLParams({ l: location }), documentViewerConfig: obj },
                l: location,
                h: history,
            })
        }
    }

    const serverIsWatching = watchers.some((watcher) => watcher.userId === currentUserId)
    const isWatching = watchOverride !== null ? watchOverride : serverIsWatching

    const handleToggleWatch = () => {
        const nextIsWatching = !isWatching
        setWatchOverride(nextIsWatching)
        if (isWatching) {
            dispatchHandler(dispatch, "removeTaskWatcherRequest", { taskId: task.id, userId: currentUserId })
        } else {
            dispatchHandler(dispatch, "addTaskWatcherRequest", { taskId: task.id, userId: currentUserId })
        }
    }

    const handleAddWatcher = (person) => {
        dispatchHandler(dispatch, "addTaskWatcherRequest", { taskId: task.id, userId: person.userId })
        setWatcherSearch('')
        setAddWatcherOpen(false)
    }

    const handleCompleteTask = () => {
        dispatchHandler(dispatch, "completeTaskRequest", { id: task.id })
        setMoreMenuOpen(false)
        setReassignSearch('')
    }

    const handleReassignTask = (person) => {
        dispatchHandler(dispatch, "reassignTaskRequest", { id: task.id, assignee: person.userId })
        setMoreMenuOpen(false)
        setReassignSearch('')
    }

    return (
        <div className="h-full overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-2">
                    <span className="text-xs border border-borderSlateGrey rounded-[3px] px-2 py-1 font-medium text-fontBlack">{taskNo}</span>
                    {childTasks.length > 0 && (
                        <div ref={childTasksRef} className="relative">
                            <button
                                type="button"
                                onClick={() => setChildTasksOpen((prev) => !prev)}
                                className="flex items-center gap-1.5 text-xs font-medium text-primary underline border border-borderSlateGrey rounded-[3px] px-2 py-1"
                            >
                                <Link2 size={13} />
                                <LanguageTranslator>{childTasks.length} Linked Task{childTasks.length !== 1 ? 's' : ''}</LanguageTranslator>
                            </button>
                            {childTasksOpen && (
                                <div className="absolute top-[calc(100%+6px)] left-0 z-[4] bg-white border border-borderSlateGrey shadow-xl rounded-[8px] w-[200px] max-h-[320px] p-5">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Link2 size={16} className="text-fontSlateGrey" />
                                        <p className="mb-0 text-[12px] font-medium text-fontBlack">
                                            <LanguageTranslator>{childTasks.length} Linked task{childTasks.length !== 1 ? 's' : ''}</LanguageTranslator>
                                        </p>
                                    </div>
                                    <div className="flex flex-col gap-3 px-1 max-h-[240px] overflow-y-auto">
                                        {childTasks.map((childTask) => (
                                            <div
                                                key={childTask.id}
                                                onClick={() => handleChildTaskClick(childTask)}
                                                className="flex items-center justify-between gap-3 cursor-pointer hover:bg-[#F8FAFC] rounded-[6px] -mx-1 px-1 py-0.5"
                                            >
                                                <span className="text-xs border border-borderSlateGrey rounded-[6px] px-4 py-1 font-medium text-fontBlack bg-[#F8FAFC]">{childTask.task_number}</span>
                                                <GenericStatusComponent status={childTask.status} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <GenericStatusComponent status={task.status} />
                </div>
                <div className="flex items-center gap-3">
                    <div ref={watchWrapperRef} className="relative flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleToggleWatch}
                            className={`flex items-center gap-1.5 text-[11px] font-medium rounded-full px-3 py-1.5 transition-colors duration-300 ease-in-out ${isWatching ? 'bg-[#E7F8EF] text-[#12B76A]' : 'bg-[#F1F4F9] text-fontSlateGrey'}`}
                        >
                            <span className="relative inline-flex items-center justify-center w-[14px] h-[14px]">
                                <Eye size={14} className={`absolute transition-all duration-300 ease-in-out ${isWatching ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} />
                                <EyeOff size={14} className={`absolute transition-all duration-300 ease-in-out ${isWatching ? 'opacity-0 scale-75' : 'opacity-100 scale-100'}`} />
                            </span>
                        </button>
                        <div className="flex items-center -space-x-2">
                            {watchers.map((watcher) => (
                                <button
                                    key={watcher.userId}
                                    type="button"
                                    onClick={() => setWatchDropdownOpen((prev) => !prev)}
                                >
                                    <GenericAvatarComponent name={watcher.name} size={25} className="text-[11px] border-2 border-white" />
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => setWatchDropdownOpen((prev) => !prev)}
                                className="relative z-[1] w-[17px] h-[17px] shrink-0 appearance-none rounded-full flex items-center justify-center bg-[#F1F4F9] text-fontSlateGrey border border-borderSlateGrey"
                            >
                                <Plus size={13} />
                            </button>
                        </div>
                        {watchDropdownOpen && (
                            <div className="absolute top-[calc(100%+6px)] right-0 z-[4] bg-white border border-borderSlateGrey shadow-xl rounded-[6px] w-[220px] py-2">
                                <div className="px-2 pb-2">
                                    <button
                                        type="button"
                                        onClick={handleToggleWatch}
                                        className="w-full flex items-center justify-center gap-1.5 border border-borderSlateGrey rounded-[4px] py-1.5 text-xs font-medium text-fontBlack hover:bg-[#F8FAFC]"
                                    >
                                        {isWatching ? <EyeOff size={13} /> : <Eye size={13} />}
                                        <LanguageTranslator>{isWatching ? 'Stop Watching' : 'Start Watching'}</LanguageTranslator>
                                    </button>
                                </div>
                                {watchers.length > 0 && (
                                    <p className="px-3 mb-1 text-[11px] text-fontSlateGrey">Watching this Task</p>
                                )}
                                <div className="max-h-[160px] overflow-y-auto">
                                    {watchers.length ? watchers.map((watcher) => (
                                        <div key={watcher.userId} className="flex items-center gap-2 px-3 py-1.5 group">
                                            <GenericAvatarComponent name={watcher.name} size={24} className="text-[10px]" />
                                            <p className="mb-0 text-xs text-fontBlack flex-1 truncate">{watcher.name}</p>
                                            <button
                                                type="button"
                                                onClick={() => dispatchHandler(dispatch, "removeTaskWatcherRequest", { taskId: task.id, userId: watcher.userId })}
                                                className="shrink-0 text-fontSlateGrey hover:text-danger opacity-0 group-hover:opacity-100"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    )) : (
                                        <NoDataFound
                                            className="!h-auto !p-2 [&_svg]:!w-[70px] [&_svg]:!h-[70px] [&_h3]:!text-[12px] [&_h3]:!mt-1"
                                            primaryText="No Watchers Yet"
                                        />
                                    )}
                                </div>
                                <div className="border-t border-borderSlateGrey mt-1 pt-2 px-2">
                                    {addWatcherOpen ? (
                                        <input
                                            autoFocus
                                            type="text"
                                            value={watcherSearch}
                                            onChange={(e) => setWatcherSearch(e.target.value)}
                                            onBlur={() => { setAddWatcherOpen(false); setWatcherSearch('') }}
                                            placeholder="Enter Name"
                                            className="w-full border border-borderSlateGrey rounded-[4px] px-2.5 py-1.5 text-xs text-fontBlack focus:outline-none"
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setAddWatcherOpen(true)}
                                            className="w-full flex items-center justify-center gap-1.5 border border-borderSlateGrey rounded-[4px] py-1.5 text-xs font-medium text-fontBlack hover:bg-[#F8FAFC]"
                                        >
                                            <Plus size={13} /> <LanguageTranslator>Add Watcher</LanguageTranslator>
                                        </button>
                                    )}
                                    {addWatcherOpen && watcherSearch && (
                                        <div className="mt-1 border border-borderSlateGrey rounded-[4px] max-h-[120px] overflow-y-auto">
                                            {filteredSuggestions.length ? (
                                                filteredSuggestions.map((person) => (
                                                    <button
                                                        key={person.userId || person.initials}
                                                        type="button"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => handleAddWatcher(person)}
                                                        className="w-full text-left px-2.5 py-1.5 text-xs text-fontBlack hover:bg-primary_w_less_opacity"
                                                    >
                                                        {person.name}
                                                    </button>
                                                ))
                                            ) : (
                                                <p className="px-2.5 py-1.5 mb-0 text-xs text-fontSlateGrey">No matches</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <Share2 size={16} className="text-fontSlateGrey cursor-pointer" onClick={() => setSelectedModal('shareDataModal')} />
                    <div ref={moreMenuRef} className="relative">
                        <button type="button" onClick={() => setMoreMenuOpen(!moreMenuOpen)} className="flex items-center text-fontSlateGrey hover:text-fontBlack">
                            <MoreVertical size={16} />
                        </button>
                        {moreMenuOpen && (
                            <div className="absolute top-[calc(100%+4px)] right-0 z-[4] bg-white border border-borderSlateGrey shadow-xl rounded-[8px] w-[260px] p-10">
                                {task.status?.toUpperCase() !== 'COMPLETED' && (
                                    <button
                                        type="button"
                                        onClick={handleCompleteTask}
                                        className="flex w-full items-center justify-center gap-1.5 bg-[#E7F8EF] text-[#12B76A] rounded-[5px] py-2 text-[12px] font-medium"
                                    >
                                        <CheckCheck size={14} />
                                        <LanguageTranslator>Mark Closed</LanguageTranslator>
                                    </button>
                                )}
                                <div className={task.status?.toUpperCase() !== 'COMPLETED' ? 'border-t border-borderSlateGrey mt-3 pt-3' : ''}>
                                    <p className="mb-2 text-[12px] text-fontSlateGrey"><LanguageTranslator>Reassign task to</LanguageTranslator></p>
                                    <div className="relative mb-2">
                                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fontSlateGrey" />
                                        <input
                                            type="text"
                                            value={reassignSearch}
                                            onChange={(e) => setReassignSearch(e.target.value)}
                                            placeholder="Search"
                                            className="w-full border border-borderSlateGrey rounded-[6px] pl-8 pr-2.5 py-1.5 text-xs text-fontBlack focus:outline-none"
                                        />
                                    </div>
                                    <div className="max-h-[180px] overflow-y-auto flex flex-col gap-2">
                                        {reassignCandidates.length ? reassignCandidates.map((person) => (
                                            <button
                                                key={person.userId}
                                                type="button"
                                                onClick={() => handleReassignTask(person)}
                                                className="w-full flex items-center gap-2 px-1 py-1 rounded-[4px] hover:bg-[#F8FAFC] text-left"
                                            >
                                                <GenericAvatarComponent name={person.name} size={28} className="text-[11px]" />
                                                <span className="text-[13px] text-fontBlack">{person.name}</span>
                                            </button>
                                        )) : (
                                            <p className="px-1 py-1.5 mb-0 text-xs text-fontSlateGrey">No matches</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="px-5">
                <p className="mb-1 text-[20px] font-semibold text-fontBlack">{task.title}</p>
                <p className="mb-4 text-[12px] font-normal text-fontSlateGrey">{task.description}</p>

                <div className="border-t border-borderSlateGrey pt-4 mb-4">
                    <p className="mb-2 text-[11px] text-fontSlateGrey">Created by</p>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="flex items-center gap-2">
                            <GenericAvatarComponent name={assignedBy?.name} initials={assignedBy?.initials} size={28} />
                            <div>
                                <p className="mb-0 text-[12px] font-medium text-fontBlack">{assignedBy?.name || 'NA'}</p>
                                <p className="mb-0 text-[12px] text-fontSlateGrey">{assignedBy?.role || 'NA'}</p>
                            </div>
                        </div>
                        <div>
                            <p className="mb-1 flex items-center gap-1.5 text-[11px] text-fontSlateGrey"><User size={12} /> Assigned To</p>
                            <div className="flex items-center gap-1">
                                <TextWithTooltip text={task.assignee_name || 'NA'} width={140} className="text-[11px] text-fontBlack" />
                            </div>
                        </div>
                        <div>
                            <p className="mb-1 flex items-center gap-1.5 text-[11px] text-fontSlateGrey"><Phone size={12} /> Mobile Number</p>
                            <div className="flex items-center gap-1">
                                <p className="mb-0 text-[11px] text-fontBlack">{task.assigned_by_phone || 'NA'}</p>
                                <CopyContentGenericComponent type={2} content={task.assigned_by_phone} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                        <p className="mb-1 flex items-center gap-1.5 text-[11px] text-fontSlateGrey"><Calendar size={12} /> Creation Date</p>
                        <p className="mb-0 text-[11px] text-fontBlack">{creationDate || 'NA'}</p>
                    </div>
                    <div>
                        <p className="mb-1 flex items-center gap-1.5 text-[11px] text-fontSlateGrey"><Calendar size={12} /> Due Date</p>
                        <p className="mb-0 text-[11px] text-fontBlack">{dueDate || 'NA'}</p>
                    </div>
                    <div ref={priorityRef} className="relative">
                        <p className="mb-1 flex items-center gap-1.5 text-[11px] text-fontSlateGrey"><LayoutGrid size={12} /> Priority</p>
                        <button
                            type="button"
                            onClick={() => setPriorityOpen(!priorityOpen)}
                            className={`flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 font-medium ${PRIORITY_STYLE_MAP[task.priority]}`}
                        >
                            {PriorityIcon && <PriorityIcon size={12} />}
                            <LanguageTranslator>{task.priority}</LanguageTranslator>
                        </button>
                        {priorityOpen && (
                            <div className="absolute top-[calc(100%+4px)] left-0 z-[4] bg-white border border-borderSlateGrey shadow-xl rounded-[3px] py-1 w-[110px]">
                                {priorityOptions.map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => { props.onPriorityChange?.(option); setPriorityOpen(false) }}
                                        className={`w-full flex items-center justify-between gap-2 text-left px-3 py-1.5 text-xs hover:bg-primary_w_less_opacity ${task.priority === option ? 'text-primary font-medium' : 'text-fontBlack'}`}
                                    >
                                        {option}
                                        {task.priority === option && <Check size={12} />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="border-t border-borderSlateGrey pt-4 flex items-start gap-10">
                    <div
                        className={`w-[90px] h-[90px] rounded-[8px] border border-borderSlateGrey bg-white p-1 shrink-0 flex items-center justify-center ${isFirstMediaImage ? 'cursor-pointer' : ''}`}
                        onClick={isFirstMediaImage ? () => setSelectedModal('mediaView') : undefined}
                    >
                        {isFirstMediaImage ? (
                            <img src={firstMedia.fileUrl} alt="attachment" className="w-full h-full rounded-[4px] object-cover" />
                        ) : task.product?.image ? (
                            <img src={task.product.image} alt={task.product?.name} className="w-full h-full rounded-[4px] object-cover" />
                        ) : (
                            <Icons.ImageThumbnail />
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-4 flex-1">
                        {detailFields.map((field) => (
                            <div key={field.key}>
                                <p className="mb-1 flex items-center gap-1.5 text-[11px] text-fontSlateGrey"><Hash size={12} /> {field.label}</p>
                                {field.copyable ? (
                                    <div className="flex items-center gap-1">
                                        <TextWithTooltip
                                            as="p"
                                            width="100%"
                                            text={detailsResource[field.key] || 'NA'}
                                            className={`mb-0 text-[11px] text-primary font-medium ${field.view ? 'cursor-pointer hover:underline' : ''}`}
                                            onClick={field.view ? () => handleFieldClick(field) : undefined}
                                        />
                                        <CopyContentGenericComponent type={2} content={detailsResource[field.key]} />
                                    </div>
                                ) : (
                                    <TextWithTooltip as="p" width="100%" text={detailsResource[field.key] || 'NA'} className="mb-0 text-[11px] text-fontBlack" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {commentDocumentNo && (
                <div className="border-t border-borderSlateGrey mt-4">
                    <p className="px-5 pt-4 pb-2 mb-0 text-[13px] font-semibold text-fontBlack">Comments & Remarks</p>
                    <div className="h-[380px] border-t border-borderSlateGrey">
                        <CommentBoxModalWrapper key={task?.id} item={commentItem} comment={commentConfig} isDocumentViewer isTaskComments mode="normal" scrollToCommentId={scrollToCommentId} />
                    </div>
                </div>
            )}

            {selectedModal && (
                <div className={getModalWrapperClass()}>
                    <ParentModal
                        getParentChildProps={getParentChildProps()}
                        childComponent={getChildComponent()}
                    />
                </div>
            )}

            {childTaskPreviewId && (
                <ParentModal
                    getParentChildProps={{
                        closeModal: closeChildTaskPreview,
                        animationType: 'bottom',
                        parentModalOuterMostClassName: 'flex items-center justify-center',
                        postitionProps: { position: 'relative' },
                        task: childTaskPreview,
                    }}
                    childComponent={TaskPreviewModal}
                />
            )}
        </div>
    )
}

export default TaskManagerDetailPanel
